import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle, RefreshCw, Loader2, Eye } from 'lucide-react';
import { RECORTES_HTML_BASE } from '../../lib/recortes-html';
import { submitTask } from '../../lib/tasks';
import type { CourseTask, TaskSubmission } from '../../types/tasks';

function toNota5(score: number): string {
  return (Math.round((1 + (score / 100) * 4) * 10) / 10).toFixed(1);
}

interface TaskModalProps {
  task: CourseTask;
  studentId: string;
  existingSubmission: TaskSubmission | null;
  onClose: () => void;
  onSubmitted: (submission: TaskSubmission) => void;
}

type Phase = 'task' | 'result';

function heatColor(score: number): string {
  const h = Math.round(score * 1.2);
  return `hsl(${h} 80% 55%)`;
}

export function TaskModal({ task, studentId, existingSubmission, onClose, onSubmitted }: TaskModalProps) {
  const baseUrl = `${RECORTES_HTML_BASE}/${task.html_path}`;
  const [phase, setPhase] = useState<Phase>(existingSubmission ? 'result' : 'task');
  const [submission, setSubmission] = useState<TaskSubmission | null>(existingSubmission);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const reviewOnLoad = useRef(false);
  // Cache-buster: en revisión usamos ?v=<ts> para forzar HTML fresco desde Supabase Storage
  const reviewCacheBust = useRef('');
  const iframeUrl = phase === 'task' && reviewCacheBust.current
    ? `${baseUrl}${reviewCacheBust.current}`
    : baseUrl;

  // Capturar resultado que emite el HTML de tarea via postMessage
  useEffect(() => {
    async function handler(e: MessageEvent) {
      if (e.data?.type !== 'cubus-tarea-resultado') return;
      const res = e.data.data;
      if (typeof res?.nota !== 'number') return;
      setSaving(true);
      setSaveError('');
      try {
        const sub = await submitTask(task.id, studentId, res.nota, res);
        setSubmission(sub);
        setPhase('result');
        onSubmitted(sub);
      } catch (err: unknown) {
        setSaveError(err instanceof Error ? err.message : 'Error al guardar');
      } finally {
        setSaving(false);
      }
    }
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [task.id, studentId, onSubmitted]);

  // Cerrar con Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const lastScore = submission?.score ?? 0;
  const avg = submission?.score_avg ?? lastScore;
  const attempts = submission?.attempt_count ?? 1;
  const history = submission?.score_history ?? [];
  const feedbackMsg = avg >= 80 ? '¡Muy bien!' : avg >= 50 ? 'Podés mejorar' : 'Seguí practicando';

  return createPortal(
    <div className="fixed inset-0 z-[400] bg-black/95 flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-white/[0.08] bg-[#050505] flex-shrink-0">
        <span className="text-[10px] tracking-[2px] uppercase font-bold text-cubus-lime">
          // TAREA
        </span>
        <span className="text-[10px] text-white/40 tracking-wide truncate max-w-xs">
          {task.title}
        </span>

        {saving && (
          <span className="ml-auto flex items-center gap-1.5 text-[11px] text-cubus-lime">
            <Loader2 className="w-3 h-3 animate-spin" /> Guardando nota…
          </span>
        )}
        {saveError && (
          <span className="ml-auto text-[11px] text-rose-400">{saveError}</span>
        )}

        <button
          onClick={onClose}
          className="ml-auto flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.10] border border-white/10 text-white/80 text-sm"
        >
          Cerrar <X className="w-4 h-4" />
        </button>
      </div>

      {/* Resultado */}
      {phase === 'result' && submission ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-6 bg-[#050505]">

          {/* Nota grande: escala 1-5 */}
          <div className="text-center">
            <div className="text-[96px] font-bold leading-none" style={{ color: heatColor(avg) }}>
              {toNota5(avg)}
            </div>
            <p className="text-white/40 text-xs mt-1 uppercase tracking-widest">
              {attempts > 1 ? 'Promedio · escala 1–5' : 'Tu nota · escala 1–5'}
            </p>
            <p className="text-white/25 text-[11px] mt-0.5 font-mono">{avg}%</p>
          </div>

          <div className="text-center space-y-1">
            <p className="text-2xl font-bold text-white">{feedbackMsg}</p>
            {attempts > 1 && (
              <p className="text-white/50 text-sm">
                Último intento:{' '}
                <span style={{ color: heatColor(lastScore) }}>{toNota5(lastScore)}</span>
                {' · '}{new Date(submission.submitted_at).toLocaleDateString('es-CL')}
              </p>
            )}
            {attempts === 1 && (
              <p className="text-white/50 text-sm">
                {new Date(submission.submitted_at).toLocaleDateString('es-CL')}
              </p>
            )}
          </div>

          {/* Historial de intentos */}
          {attempts > 1 && (
            <div className="flex items-center gap-2 flex-wrap justify-center max-w-sm">
              {history.map((s, i) => (
                <span
                  key={i}
                  className="text-[11px] px-2.5 py-0.5 rounded-full border"
                  style={{ color: heatColor(s), borderColor: `${heatColor(s)}55` }}
                >
                  #{i + 1} · {toNota5(s)}
                </span>
              ))}
              <span className="text-[11px] text-white/30 ml-1">{attempts} intentos</span>
            </div>
          )}

          <div className="flex gap-3 flex-wrap justify-center">
            <button
              onClick={() => {
                reviewOnLoad.current = true;
                reviewCacheBust.current = `?v=${Date.now()}`;
                setPhase('task');
              }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.10] border border-white/10 text-white text-sm"
            >
              <Eye className="w-4 h-4" />
              Ver revisión
            </button>
            <button
              onClick={() => { reviewCacheBust.current = ''; setPhase('task'); }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.10] border border-white/10 text-white/70 text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Volver a intentar
            </button>
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-cubus-lime hover:bg-cubus-lime/80 text-[#050505] font-bold text-sm"
            >
              <CheckCircle className="w-4 h-4" />
              Listo
            </button>
          </div>

          {avg < 80 && (
            <p className="text-white/25 text-xs max-w-xs text-center">
              Podés repetir la tarea las veces que quieras — se promedia tu nota.
            </p>
          )}
        </div>
      ) : (
        <div className="flex-1 relative bg-[#050505]">
          {saving && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/70">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 text-cubus-lime animate-spin" />
                <p className="text-white text-sm">Guardando nota…</p>
              </div>
            </div>
          )}
          <iframe
            ref={iframeRef}
            src={iframeUrl}
            title={task.title}
            className="w-full h-full border-0"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            onLoad={() => {
              if (reviewOnLoad.current) {
                reviewOnLoad.current = false;
                const pasos = (submission?.result_json as { pasos?: { click?: { x: number; y: number } | null; acierto_pct?: number | null }[] } | null)?.pasos;
                const clicks = pasos?.map((p) =>
                  p.click ? { x: p.click.x, y: p.click.y, score: p.acierto_pct ?? 0 } : null,
                ) ?? [];
                const win = iframeRef.current?.contentWindow;
                setTimeout(() => {
                  win?.postMessage({ type: 'cubus-start-review', clicks }, '*');
                }, 300);
              }
            }}
          />
        </div>
      )}
    </div>,
    document.body,
  );
}
